import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { apiFetch } from "../api";
import "./SessionsList.css";

const SessionsList = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const fetchSessions = async () => {
    setLoading(true);
    setError("");

    try {
      const offset = (page - 1) * limit;
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      if (filterStatus) {
        params.append("status", filterStatus);
      }

      const response = await apiFetch(`/sessions?${params}`);

      if (!response.ok) {
        throw new Error("Failed to fetch sessions");
      }

      const data = await response.json();
      setSessions(data.sessions || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [filterStatus, page]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleNewSession = async () => {
    try {
      const response = await apiFetch("/sessions", {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Failed to create session");
      }

      const session = await response.json();
      navigate(`/sessions/${session.id}/record`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm("Are you sure you want to delete this session?")) {
      return;
    }

    try {
      const response = await apiFetch(`/sessions/${sessionId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete session");
      }

      // Refresh the list
      fetchSessions();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleNavigateToSession = (session) => {
    const { id, status } = session;

    switch (status) {
      case "recording":
        navigate(`/sessions/${id}/record`);
        break;
      case "identified":
        navigate(`/sessions/${id}/confirm`);
        break;
      case "processing":
        // Don't allow navigation during processing
        break;
      case "complete":
        navigate(`/sessions/${id}/results`);
        break;
      default:
        break;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      recording: { text: "Recording", icon: "⏺", color: "red" },
      identified: { text: "Identified", icon: "⏸", color: "yellow" },
      processing: { text: "Processing", icon: "⏳", color: "blue" },
      complete: { text: "Complete", icon: "✓", color: "green" },
    };
    return statusMap[status] || { text: status, icon: "", color: "" };
  };

  const getBatchLabels = (session) => {
    const batches =
      session.confirmed_batches ||
      session.identified_batches?.batch_labels ||
      [];
    return batches;
  };

  const getProtocolAbbreviation = (name) => {
    const abbreviations = {
      "cupdat Standard": "Std",
      "SCA Affective Assessment": "SCA",
    };
    return abbreviations[name] || name.substring(0, 3);
  };

  const getScoreColor = (protocolName, score) => {
    if (score == null) return "";
    if (protocolName === "SCA Affective Assessment") {
      if (score >= 80) return "green";
      if (score >= 60) return "yellow";
      return "red";
    }
    // cupdat Standard (1-5 scale)
    if (score >= 4) return "green";
    if (score >= 3) return "yellow";
    return "red";
  };

  const renderBatchItems = (session) => {
    const { status, batch_notes } = session;

    // For completed sessions with batch notes, show per-protocol scores
    if (status === "complete" && batch_notes && batch_notes.length > 0) {
      return (
        <div className="session-batch-list">
          {batch_notes.map((note, index) => {
            const scores = note.protocol_scores || [];
            const isMultiProtocol = scores.length > 1;
            return (
              <div key={index} className="session-batch-item">
                {note.roaster_name && (
                  <span className="session-roaster-name">
                    {note.roaster_name}
                  </span>
                )}
                <span className="session-batch-label">{note.batch_label}</span>
                {isMultiProtocol ? (
                  <div className="session-protocol-scores">
                    {scores.map((ps, i) => (
                      <span
                        key={i}
                        className={`session-protocol-badge score-${getScoreColor(ps.protocol_name, ps.score)}`}
                      >
                        {getProtocolAbbreviation(ps.protocol_name)}{" "}
                        {ps.score != null ? ps.score : "—"}
                      </span>
                    ))}
                  </div>
                ) : (
                  scores[0] && (
                    <span
                      className={`session-batch-score score-${getScoreColor(scores[0].protocol_name, scores[0].score)}`}
                    >
                      {scores[0].score != null ? scores[0].score : "—"}
                    </span>
                  )
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // For identified sessions, show batches awaiting confirmation
    if (status === "identified") {
      const batchLabels = getBatchLabels(session);
      return (
        <div className="session-batch-list">
          {batchLabels.map((label, index) => (
            <div key={index} className="session-batch-item">
              <span className="session-batch-label">{label}</span>
              <span className="session-pending-note">
                Awaiting confirmation
              </span>
            </div>
          ))}
        </div>
      );
    }

    // For other statuses, show batch labels if available
    const batchLabels = getBatchLabels(session);
    if (batchLabels.length > 0) {
      return (
        <div className="session-batches">
          {batchLabels.slice(0, 3).join(", ")}
          {batchLabels.length > 3 && (
            <span className="more-batches">
              {" "}
              + {batchLabels.length - 3} more
            </span>
          )}
        </div>
      );
    }

    return null;
  };

  const getActionButton = (session) => {
    const { status } = session;

    switch (status) {
      case "recording":
        return (
          <button
            onClick={() => handleNavigateToSession(session)}
            className="btn-primary"
          >
            Continue Recording
          </button>
        );
      case "identified":
        return (
          <button
            onClick={() => handleNavigateToSession(session)}
            className="btn-primary"
          >
            Confirm Batches
          </button>
        );
      case "processing":
        return (
          <button className="btn-disabled" disabled>
            Processing...
          </button>
        );
      case "complete":
        return (
          <button
            onClick={() => handleNavigateToSession(session)}
            className="btn-primary"
          >
            View Results
          </button>
        );
      default:
        return null;
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="dashboard-container">
      <Header user={user} onLogout={handleLogout} />

      <main className="dashboard-content">
        <div className="sessions-header">
          <h2>Cupping Sessions</h2>
          <button onClick={handleNewSession} className="btn-new-session">
            + New Session
          </button>
        </div>

        <div className="filter-section">
          <button
            className={`filter-btn ${filterStatus === "" ? "active" : ""}`}
            onClick={() => setFilterStatus("")}
          >
            All
          </button>
          <button
            className={`filter-btn ${
              filterStatus === "recording" ? "active" : ""
            }`}
            onClick={() => setFilterStatus("recording")}
          >
            Recording
          </button>
          <button
            className={`filter-btn ${
              filterStatus === "identified" ? "active" : ""
            }`}
            onClick={() => setFilterStatus("identified")}
          >
            Identified
          </button>
          <button
            className={`filter-btn ${
              filterStatus === "complete" ? "active" : ""
            }`}
            onClick={() => setFilterStatus("complete")}
          >
            Complete
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <div className="loading">Loading sessions...</div>
        ) : sessions.length === 0 ? (
          <div className="empty-state">
            <p>
              {filterStatus
                ? `No sessions found with status: ${filterStatus}`
                : "No cupping sessions yet"}
            </p>
            {!filterStatus && (
              <button onClick={handleNewSession} className="btn-primary">
                Create Your First Session
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="sessions-list">
              {sessions.map((session) => {
                const statusDisplay = getStatusDisplay(session.status);

                return (
                  <div key={session.id} className="session-card">
                    <div className="session-header">
                      <div className="session-date">
                        {formatDate(session.created_at)}
                      </div>
                      <div
                        className={`session-status status-${statusDisplay.color}`}
                      >
                        <span className="status-icon">
                          {statusDisplay.icon}
                        </span>
                        {statusDisplay.text}
                      </div>
                    </div>

                    <div className="session-info">
                      {renderBatchItems(session)}
                    </div>

                    <div className="session-actions">
                      {getActionButton(session)}
                      <button
                        onClick={() => handleDeleteSession(session.id)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="pagination-btn"
                >
                  ← Previous
                </button>
                <span className="pagination-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="pagination-btn"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default SessionsList;
